import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  Typography,
} from '@material-ui/core';

import PropTypes from 'prop-types';

import AddButton from '../buttons/addButton';
import AddMembersDialog from '../modals/addMembersDialog';
import CustomSnackbar from '../feedback/customSnackbar';
import MemberInfo from './memberInfo';
import SpinnerLoading from '../feedback/spinnerLoading';

import { modifyEnrolmentTeamAdmin } from '../../utils/comms';

import translations from '../../languages/i18n';

import { SessionContext } from '../../utils/context';

export default function TeamSection({ enrolmentData, handleReloadEnrolments, programAdministratorIds, title, userType }) {
  const sessionContext = useContext(SessionContext);

  const [currentMembers, setCurrentMembers] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    setCurrentMembers(enrolmentData.supervisedBy);
  }, [enrolmentData]); // el useEffect només s'executa si enrolmentData ha canviat

  const manageFeedback = (fbType, fbMessage, reload) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
    // Si s'ha de recarregar cridam la funció pertinent al pare
    // el nou enrolmentData que obtindrà el pare es propagarà als fills que actualitzen el seu al useEffect corresponent
    if (reload) handleReloadEnrolments();
  };

  const modifyAdmin = async (teamMembers) => {
    setShowLoading(true);
    const response = await modifyEnrolmentTeamAdmin(teamMembers, enrolmentData._id, sessionContext.userToken);
    setShowLoading(false);
    if (response.status === 200) { // Operació realitzada correctament
      // setCurrentMembers(response.data.enrolment.supervisedBy);
      manageFeedback('success', 'SetClinicianToAdmin', true);
    } else if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
      manageFeedback('error', 'TimeOut', false);
    } else { // Error amb les dades o al servidor
      manageFeedback('error', 'SetClinicianToAdmin', false);
    }
  };

  const makeAdmin = (idClinician, admin) => {
    if (!admin) { // Si l'usuari ja és admin no feim res
      // NOTA: necessit fer un deep copy (que els canvis que faig a la còpia no afectin a l'original) de currentMembers però com conté elements anidats (és una array d'objectes) i a més forma part de l'state
      // no puc emprar "const currentMembersCopy = [...currentMembers];" ni "const currentMembersCopy = currentMembers.slice();" ja que només fan shallow copy (quan faig un canvi a un també és modifica l'altre).
      // En aquests casos, per fer un deep copy he d'emprar JSON.parse(JSON.stringify(object)). Aquesta manera de copiar té algunes limitacions (no funciona si el que volem copiar té mètodes o objectes circulars)
      // que en aquest cas no m'afecten
      const currentMembersCopy = JSON.parse(JSON.stringify(currentMembers));
      // A la còpia de currentMembers, posam tots els admins a false ja que conserva a true l'admin antic, de quan hem carregat el component
      currentMembersCopy.forEach((current) => current.admin = false);
      // Posam a true l'admin del clínic sobre el que s'ha fet clic
      const idx = currentMembersCopy.findIndex((current) => current.clinician._id === idClinician);
      currentMembersCopy[idx].admin = true;
      // Preparam l'array per enviar al servidor
      const teamMembers = [];
      currentMembersCopy.forEach((current) => teamMembers.push({ clinician: current.clinician._id, admin: current.admin }));
      modifyAdmin(teamMembers);
    }
  };

  return (
    <React.Fragment>
      <SpinnerLoading show={showLoading} />
      <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && feedbackMessage) || ''} />
      {/* Pos showAddEvent && per evitar que s'executi AddEventDialog fins que no obro el AddEvent */}
      {showAddMembers && (
        <AddMembersDialog
          open={showAddMembers}
          title={translations['_addMembersDialogTitle']}
          currentMembers={currentMembers}
          idEnrolment={enrolmentData._id}
          idProgram={enrolmentData.program._id}
          handleClose={() => setShowAddMembers(false)}
          handleFeedback={(fbType, fbMessage, reload) => manageFeedback(fbType, fbMessage, reload)}
        />
      )}
      <Grid container justify='flex-start' spacing={2}>
        <Grid item xs={10}>
          <Typography variant='h6' component='div'>{title}</Typography>
        </Grid>
        {(currentMembers.length !== 0 && (
          currentMembers.map((member) => (
            member.clinician.active && ( // Només mostram els clinics actius (el que no han estat "eliminats" del sistema)
              <Grid item xs={10} key={`grid${member.clinician._id}`}>
                <MemberInfo
                  key={member.clinician._id}
                  userType={userType}
                  idEnrolment={enrolmentData._id}
                  memberData={member.clinician}
                  programAdministratorIds={programAdministratorIds}
                  teamAdministrator={member.admin}
                  handleFeedback={(fbType, fbMessage, reload) => manageFeedback(fbType, fbMessage, reload)}
                  handleMakeAdmin={(idClinician, admin) => makeAdmin(idClinician, admin)}
                />
              </Grid>
            )
          ))
        )) || (
          <Grid item xs={10}>
            <Typography variant='body1' component='div'>{translations['_emptyTeam']}</Typography>
          </Grid>
        )}
        <Grid item xs={10}>
          {/* Si el clínic es de tipus bàsic no pot afegir membres */}
          {/* TODO: decidir què fer amb l'usuari AppAdmin (l'administrador del sistema) */}
          <AddButton disabled={userType === 'Basic'} handleClick={() => setShowAddMembers(true)} />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

// Indica i controla els props que se han de passar al component
TeamSection.propTypes = {
  userType: PropTypes.string.isRequired,
  enrolmentData: PropTypes.object.isRequired,
  handleReloadEnrolments: PropTypes.func.isRequired,
  programAdministratorIds: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
};
